import React, { useCallback } from 'react';
import { Table, Button, Input, Select, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FN110, FN117 } from '../../../services/n202Api';
import { fileTypeLabel } from '../helpers';

interface Tab7Props {
  formData: Partial<FN110>;
  rows: Partial<FN117>[];
  isEditing: boolean;
  onRowsChange: (updater: (prev: Partial<FN117>[]) => Partial<FN117>[]) => void;
}

const fileTypeOptions = [
  { value: 1, label: 'Word' },
  { value: 2, label: 'Excel' },
  { value: 3, label: 'PPT' },
  { value: 4, label: 'HTML' },
  { value: 5, label: 'PDF' },
];

const Tab7Attachments: React.FC<Tab7Props> = ({
  formData, rows, isEditing, onRowsChange,
}) => {
  const handleCellChange = useCallback((idx: number, key: keyof FN117, value: FN117[keyof FN117]) => {
    onRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }, [onRowsChange]);

  const handleAdd = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fn117_02 || 0), 0);
    onRowsChange(prev => [...prev, {
      fn117_01: formData.fn110_01,
      fn117_02: maxSeq + 1,
      fn117_21: 5, // Default PDF
    }]);
  }, [rows, formData.fn110_01, onRowsChange]);

  const handleDelete = useCallback((idx: number) => {
    onRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn117_02: i + 1 })));
  }, [onRowsChange]);

  const columns: ColumnsType<Partial<FN117>> = [
    { title: <span data-contract-field="fn117_02">#</span>, width: 50, render: (_v, _r, i) => i + 1 },
    {
      title: <span data-contract-field="fn117_11">檔案名稱</span>, dataIndex: 'fn117_11',
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn117_11', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn117_21">檔案類型</span>, dataIndex: 'fn117_21', width: 120,
      render: (v, _r, i) => isEditing
        ? <Select size="small" value={v} onChange={val => handleCellChange(i, 'fn117_21', val)} style={{ width: '100%' }} options={fileTypeOptions} />
        : fileTypeLabel(v),
    },
    {
      title: <span data-contract-field="fn117_22">檔案路徑</span>, dataIndex: 'fn117_22',
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn117_22', e.target.value)} />
        : v,
    },
    ...(isEditing ? [{
      title: '操作', width: 60, align: 'center' as const,
      render: (_v: unknown, _r: Partial<FN117>, i: number) => (
        <Popconfirm title="確認刪除?" onConfirm={() => handleDelete(i)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', marginBottom: 12, alignItems: 'center', maxWidth: 300 }}>
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>附件筆數</span>
        <Input size="small" disabled value={String(rows.length || 0)} style={{ maxWidth: 80 }} />
      </div>
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增附件</Button>
        </Space>
      )}
      <Table<Partial<FN117>>
        columns={columns}
        dataSource={rows}
        rowKey={(_r, i) => String(i)}
        size="small"
        pagination={false}
        bordered
        scroll={{ y: 400 }}
      />
    </div>
  );
};

export default React.memo(Tab7Attachments);
